/* ============================================================
   CARINA — product-form.js
   Produktformular: Varianten, Warenkorb-AJAX
   ============================================================ */

'use strict';

class ProductFormComponent extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form[data-product-form]');
    this.variantSelectors = this.querySelectorAll('[data-variant-select]');
    this.addBtn = this.querySelector('[data-add-to-cart]');
    this.btnText = this.querySelector('[data-btn-text]');
    this.priceEl = this.querySelector('[data-product-price]');
    this.comparePriceEl = this.querySelector('[data-compare-price]');
    this.availabilityEl = this.querySelector('[data-availability]');
    this.errorEl = this.querySelector('[data-form-error]');

    if (this.form) {
      this.form.addEventListener('submit', (e) => this._handleSubmit(e));
    }

    // Variant change
    this.variantSelectors.forEach((selector) => {
      selector.addEventListener('change', () => this._onVariantChange());
    });

    this._initVariant();
  }

  _initVariant() {
    // Set initial state based on preselected variant
    const selectedVariant = this._getSelectedVariant();
    if (selectedVariant) this._updateForVariant(selectedVariant);
  }

  _onVariantChange() {
    const variant = this._getSelectedVariant();
    if (!variant) {
      this._setUnavailable();
      return;
    }
    this._updateForVariant(variant);
    // Update URL
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  _getSelectedVariant() {
    const productData = this._getProductData();
    if (!productData) return null;
    const selectedOptions = Array.from(this.variantSelectors).map((s) => s.value);
    return productData.variants.find((v) =>
      v.options.every((opt, i) => opt === selectedOptions[i])
    ) || null;
  }

  _getProductData() {
    const script = this.querySelector('[data-product-json]');
    if (!script) return null;
    try { return JSON.parse(script.textContent); } catch { return null; }
  }

  _updateForVariant(variant) {
    // Preis seitenweit aktualisieren (Haupt- UND Sticky-Preis liegen außerhalb dieser Komponente)
    const priceStr = this._formatMoney(variant.price);
    document.querySelectorAll('[data-product-price]').forEach((el) => { el.textContent = priceStr; });
    document.querySelectorAll('[data-compare-price]').forEach((el) => {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        el.textContent = this._formatMoney(variant.compare_at_price);
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', '');
      }
    });
    // Grundpreis (PAngV) der gewählten Variante mit-aktualisieren
    // (betrifft nur die Shopify-Grundpreis-Quelle mit data-unit-price; Metafeld-Grundpreis ist produktweit statisch)
    const unitPriceStr = this._grundpreis(variant);
    document.querySelectorAll('[data-unit-price]').forEach((el) => {
      const wrap = el.closest('.unit-price');
      if (unitPriceStr) {
        el.textContent = unitPriceStr;
        if (wrap) wrap.hidden = false;
      } else if (wrap) {
        wrap.hidden = true;
      }
    });
    // Availability
    if (variant.available) {
      this._setAvailable(variant);
    } else {
      this._setSoldOut();
    }
    // Sticky-Leiste (außerhalb der Komponente) über Event synchronisieren
    document.dispatchEvent(new CustomEvent('product:variantchange', { detail: { available: !!variant.available } }));
    // Hidden input
    const idInput = this.querySelector('input[name="id"]');
    if (idInput) idInput.value = variant.id;
  }

  _setAvailable(variant) {
    if (this.addBtn) {
      this.addBtn.disabled = false;
      this.addBtn.classList.remove('is-disabled');
    }
    if (this.btnText) this.btnText.textContent = this.addBtn.dataset.labelAdd || 'In den Warenkorb';
    if (this.availabilityEl) {
      this.availabilityEl.textContent = variant.inventory_quantity > 5
        ? 'Auf Lager'
        : variant.inventory_quantity > 0
          ? `Nur noch ${variant.inventory_quantity} verfügbar`
          : 'Lieferbar';
    }
  }

  _setSoldOut() {
    if (this.addBtn) {
      this.addBtn.disabled = true;
      this.addBtn.classList.add('is-disabled');
    }
    if (this.btnText) this.btnText.textContent = 'Ausverkauft';
    if (this.availabilityEl) this.availabilityEl.textContent = 'Ausverkauft';
  }

  _setUnavailable() {
    if (this.addBtn) {
      this.addBtn.disabled = true;
      this.addBtn.classList.add('is-disabled');
    }
    if (this.btnText) this.btnText.textContent = 'Nicht verfügbar';
    document.dispatchEvent(new CustomEvent('product:variantchange', { detail: { available: false } }));
  }

  async _handleSubmit(e) {
    e.preventDefault();
    if (!this.addBtn || this.addBtn.disabled) return;

    const originalText = this.btnText ? this.btnText.textContent : '';
    this.addBtn.disabled = true;
    if (this.btnText) this.btnText.textContent = 'Wird hinzugefügt …';
    if (this.errorEl) this.errorEl.setAttribute('hidden', '');

    const formData = new FormData(this.form);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.description || 'Fehler beim Hinzufügen');
      }

      if (this.btnText) this.btnText.textContent = '✓ Hinzugefügt';
      document.dispatchEvent(new CustomEvent('cart:refresh'));

      // Cart count update
      this._refreshCartCount();

      setTimeout(() => {
        if (this.btnText) this.btnText.textContent = originalText;
        this.addBtn.disabled = false;
      }, 1800);

    } catch (err) {
      if (this.errorEl) {
        this.errorEl.textContent = err.message;
        this.errorEl.removeAttribute('hidden');
      }
      if (this.btnText) this.btnText.textContent = originalText;
      this.addBtn.disabled = false;
    }
  }

  async _refreshCartCount() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.toggleAttribute('hidden', cart.item_count === 0);
      });
    } catch {}
  }

  // Grundpreis (PAngV) einer Variante — normalisiert auf 1 kg/l/m²/m, Länge ab 100 m → je 1.000 m.
  // Spiegelt snippets/unit-price.liquid. Gibt formatierten String zurück oder null.
  _grundpreis(variant) {
    const m = variant && variant.unit_price_measurement;
    if (!variant || !variant.unit_price || !m || !m.reference_unit) return null;
    const qv = parseFloat(m.quantity_value);
    const nativeFallback = () => {
      const rv = (m.reference_value && m.reference_value !== 1) ? Math.round(m.reference_value) + ' ' : '';
      return this._formatMoney(variant.unit_price) + '/' + rv + m.reference_unit;
    };
    if (!qv || qv <= 0) return nativeFallback();
    const p = variant.price;
    const u = m.quantity_unit;
    let amount = null;
    let unit = null;
    switch (m.measured_type) {
      case 'weight':
        amount = p / (u === 'mg' ? qv / 1e6 : u === 'g' ? qv / 1000 : qv); unit = 'kg'; break;
      case 'volume':
        amount = p / (u === 'ml' ? qv / 1000 : u === 'cl' ? qv / 100 : u === 'm3' ? qv * 1000 : qv); unit = 'l'; break;
      case 'length': {
        const qm = u === 'mm' ? qv / 1000 : u === 'cm' ? qv / 100 : qv;
        if (qm >= 100) { amount = p * 1000 / qm; unit = '1.000 m'; } else { amount = p / qm; unit = 'm'; }
        break;
      }
      case 'area':
        amount = p / (u === 'cm2' ? qv / 10000 : qv); unit = 'm²'; break;
      default:
        return nativeFallback();
    }
    return this._formatMoney(Math.round(amount)) + ' / ' + unit;
  }

  _formatMoney(cents) {
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
